/* global ADV */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import Loading from '../../../Loading/LoadingView';
import RadioWhatIf from '../../../RadioWhatIf/RadioWhatIfView';
import AdvForm from '../../../../CommonComponents/AdvForm';
import ValidationErrors from '../../../ValidationErrors/ValidationErrorsContainer';
import AdvInputWrapper from '../../../../CommonComponents/AdvInputWrapper';
import AdvInput, { advInputTextChanged } from '../../../../CommonComponents/AdvInput';
import Footer from '../../../Footer/FooterContainer';
import PlanImpact from '../../../PlanImpact/PlanImpactContainer';

class TextWhatifView extends Component {
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
      storeData,
      isCanadian,
    } = this.props;

    if (isCanadian) {
      storeData(goalId, whatIfTemplate.WhatIfType)(
        {
          WhatIf: {
            EstimateExpenses: false,
          },
        },
        clientId,
      );
    }

    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;

    const impactTriggerProps = ['whatIf', 'fpId'];

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  render() {
    const {
      clientId,
      planImpactItems,
      title,
      loading,
      value,
      storeData,
      whatIfTemplate,
      goalId,
      applyWhatIf,
      fpId,
      whatIf,
      groupTitle,
      id,
      dataType,
      maxLength,
      valueStorePath,
      label,
      hasCurrency,
      hasPerMonth,
      hasPerYear,
      hasPercentage,
      hasYear,
      estimateCost,
      isCanadian,
    } = this.props;

    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          <AdvForm>
            <ValidationErrors clientId={clientId} />
            <ul className="what-ifs-form-fields what-ifs-box loaded">
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">{title}</h5>
              </li>

              {!isCanadian && (
                <>
                  <li className="what-ifs-form-field">
                    <RadioWhatIf
                      label="Estimate Automatically"
                      value="estimateCost-yes"
                      checked={estimateCost}
                      onClick={() => {
                        storeData(goalId, whatIfTemplate.WhatIfType)(
                          {
                            WhatIf: {
                              EstimateExpenses: true,
                            },
                          },
                          clientId,
                        );
                      }}
                    />
                  </li>

                  <li className="what-ifs-form-field">
                    <RadioWhatIf
                      label="Use my own estimate"
                      value="estimateCost-no"
                      checked={!estimateCost}
                      onClick={() => {
                        storeData(goalId, whatIfTemplate.WhatIfType)(
                          {
                            WhatIf: {
                              EstimateExpenses: false,
                            },
                          },
                          clientId,
                        );
                      }}
                    />
                  </li>
                </>
              )}

              {!estimateCost && (
                <li className="what-ifs-form-field">
                  <label className="what-ifs-textual-label" htmlFor={id}>
                    <span className="what-ifs-label-text">{label}</span>
                    <br />
                    <AdvInputWrapper
                      hasCurrency={hasCurrency}
                      hasPerMonth={hasPerMonth}
                      hasPerYear={hasPerYear}
                      hasPercentage={hasPercentage}
                      hasYear={hasYear}
                      className="what-ifs-input-wrapper"
                    >
                      <AdvInput
                        id={id}
                        type="text"
                        dataType={dataType}
                        maxLength={maxLength}
                        onBlur={advInputTextChanged(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          valueStorePath,
                        )}
                        value={ADV.util.formatValue(value, dataType)}
                        className="what-ifs-input"
                      />
                    </AdvInputWrapper>
                  </label>
                </li>
              )}
            </ul>
          </AdvForm>
        </div>

        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />

        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
      </div>
    );
  }
}

TextWhatifView.defaultProps = {
  title: 'Untitled What If',
  groupTitle: 'Untitled What If Group',
  dataType: 'money',
  maxLength: 14,
  hasCurrency: false,
  hasPerMonth: false,
  hasPerYear: false,
  hasPercentage: false,
  hasYear: false,
};

TextWhatifView.propTypes = {
  fetchData: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  fpId: PropTypes.string.isRequired,
  goalId: PropTypes.string.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  whatIf: PropTypes.shape({
    Amount: PropTypes.string.isRequired,
    GoalId: PropTypes.string.isRequired,
    IsInvalidated: PropTypes.bool.isRequired,
    WhatIfKey: PropTypes.string.isRequired,
    WhatIfType: PropTypes.number.isRequired,
  }).isRequired,
  whatIfTemplate: PropTypes.shape({
    Amount: PropTypes.string.isRequired,
    GoalId: PropTypes.string.isRequired,
    IsInvalidated: PropTypes.bool.isRequired,
    WhatIfKey: PropTypes.string.isRequired,
    WhatIfType: PropTypes.number.isRequired,
  }).isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      current: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  title: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  storeData: PropTypes.func.isRequired,
  applyWhatIf: PropTypes.func.isRequired,
  groupTitle: PropTypes.string,
  id: PropTypes.string.isRequired,
  dataType: PropTypes.string,
  maxLength: PropTypes.number,
  valueStorePath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hasCurrency: PropTypes.bool,
  hasPerMonth: PropTypes.bool,
  hasPerYear: PropTypes.bool,
  hasPercentage: PropTypes.bool,
  hasYear: PropTypes.bool,
  isCanadian: PropTypes.bool.isRequired,
};

export default TextWhatifView;
